import React, {ReactElement, useCallback, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

// material-ui
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import {Card, Dialog, DialogActions, DialogContent, useTheme} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';

import {useQuery} from 'react-query';
import {FormService} from "crqlar-react-common/dist/services/forms/form.service";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import {FormSubmission, QuestionType} from "crqlar-react-common/dist/models/forms/form";
import {ListColumn} from "crqlar-react-common/dist/components/CList/CList";
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";
import {useList} from "crqlar-react-common/dist/components/CList/useList";
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import LinkIcon from '@mui/icons-material/Link';
import DownloadIcon from '@mui/icons-material/Download';
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import SubmissionDetailDialog from "./submission-detail/SubmissionDetailDialog";
import GenerateLinkDialog from "./GenerateLinkDialog";
import SettingsPageHeader from "../../system/layout/SettingsPageHeader";
// assets

// ==============================|| FORM SUBMISSIONS ||============================== //

interface Props {
    profileId?: string;
    hideActionBar?: boolean;
    hideTitle?: boolean;
}

export default function FormSubmissions(props: Props): ReactElement {
    const {id: formId} = useParams();
    const theme = useTheme();
    const {t} = useTranslation();
    const navigate = useNavigate();

    // Fetch form data to get questions
    const formQuery = useQuery({
        queryKey: ['form', formId],
        queryFn: async () => {
            if (!formId) throw new Error('Form ID is required');
            return await FormService.getFormById(formId);
        },
        enabled: !!formId
    });

    // State to store unique question IDs found in submissions
    const [uniqueQuestionIds, setUniqueQuestionIds] = React.useState<string[]>([]);

    // State for media preview dialog
    const [previewMedia, setPreviewMedia] = React.useState<Media | null>(null);
    const [previewDialogOpen, setPreviewDialogOpen] = React.useState(false);

    // State for submission detail dialog
    const [selectedSubmission, setSelectedSubmission] = React.useState<FormSubmission | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = React.useState(false);

    // State for generate link dialog
    const [generateLinkDialogOpen, setGenerateLinkDialogOpen] = React.useState(false);

    // Helper function to get question title by ID from form data
    const getQuestionTitle = useCallback((questionId: string): string => {
        if (!formQuery.data?.questions) return questionId;
        const question = formQuery.data.questions.find(q => q.id === questionId);
        return question?.title || questionId;
    }, [formQuery.data?.questions]);

    // Helper function to check if a value is a Media object
    const isMedia = (value: any): value is Media => {
        return value && typeof value === 'object' && 'mediaURI' in value;
    };

    // Helper function to check if a value is an array of Media objects
    const isMediaArray = (value: any): value is Media[] => {
        return Array.isArray(value) && value.length > 0 && isMedia(value[0]);
    };

    // Handle media preview
    const handleMediaClick = (media: Media) => {
        setPreviewMedia(media);
        setPreviewDialogOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewDialogOpen(false);
        setPreviewMedia(null);
    };

    // Handle media download
    const handleDownload = (media: Media) => {
        if (media.mediaURI) {
            window.open(media.mediaURI, '_blank');
        }
    };

    // Handle submission detail view
    const handleViewSubmission = (submission: FormSubmission) => {
        setSelectedSubmission(submission);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setSelectedSubmission(null);
    };

    // Columns configuration - will be populated dynamically
    const columns: ListColumn[] = useMemo(() => {
        const baseColumns: ListColumn[] = [
            {
                id: 'actions',
                name: t('Actions'),
                width: 'm'
            },
            {
                id: 'submittedAt',
                name: t('Submitted At'),
                width: 'l'
            },
            {
                id: 'email',
                name: t('Email'),
                width: 'l'
            },
            {
                id: 'phone',
                name: t('Phone'),
                width: 'l'
            }
        ];

        // Add Form Name column when profileId is provided
        if (props.profileId) {
            baseColumns.push({
                id: 'formName',
                name: t('Form Name'),
                width: 'xl'
            });
        }

        // Add dynamic columns for each unique question ID with header wrapping enabled
        const questionColumns: ListColumn[] = uniqueQuestionIds.filter(questionId =>
            formQuery.data?.questions?.find(q => q.id === questionId)?.type !== QuestionType.WELCOME
        ).map(questionId => ({
            id: `answer_${questionId}`,
            name: getQuestionTitle(questionId),
            width: 'xl',
            headerWrap: true
        }));

        return [...baseColumns, ...questionColumns];
    }, [uniqueQuestionIds, formQuery.data?.questions, props.profileId, t, getQuestionTitle]);

    // Render column function
    const renderCell = (item: FormSubmission, column: ListColumn) => {
        switch (column.id) {
            case 'actions':
                return (
                    <IconButton
                        size="small"
                        onClick={() => handleViewSubmission(item)}
                        sx={{
                            color: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'primary.lighter'
                            }
                        }}
                    >
                        <VisibilityIcon/>
                    </IconButton>
                );
            case 'submittedAt':
                return (
                    <Typography variant="body2">
                        {formatDateDMYHM(item.insertDate)}
                    </Typography>
                );
            case 'email':
                return (
                    <Typography variant="body2">
                        {item.email || '-'}
                    </Typography>
                );
            case 'phone':
                return (
                    <Typography variant="body2">
                        {item.phone || '-'}
                    </Typography>
                );
            case 'formName':
                return (
                    <Typography variant="body2">
                        {item.formName || '-'}
                    </Typography>
                );
            default:
                // Handle dynamic answer columns
                if (column.id.startsWith('answer_')) {
                    const questionId = column.id.replace('answer_', '');
                    // Try to find answer by questionId first, fallback to questionTitle
                    const answer = item.answers?.find(a =>
                        ('questionId' in a && a.questionId === questionId) ||
                        a.questionTitle === getQuestionTitle(questionId)
                    );

                    if (!answer) return <Typography variant="body2">-</Typography>;

                    // Handle different answer types
                    // Check for Media or Media[] first
                    if (isMedia(answer.answer)) {
                        const media = answer.answer as Media;

                        return (
                            <Box
                                onClick={() => handleMediaClick(media)}
                                sx={{
                                    cursor: 'pointer',
                                    display: 'inline-block',
                                    '&:hover': {opacity: 0.8}
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={getScaledUrl(media, MediaSize.MEDIUM_300, MediaSize.MEDIUM_300)}
                                    alt={media.publicId || 'Image'}
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 1,
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                        );
                    } else if (isMediaArray(answer.answer)) {
                        const mediaArray = answer.answer as Media[];
                        return (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {mediaArray.map((media, index) => {

                                    return (
                                        <Box
                                            key={index}
                                            onClick={() => handleMediaClick(media)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {opacity: 0.8}
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image={getScaledUrl(media, MediaSize.MEDIUM_300, MediaSize.MEDIUM_300)}
                                                alt={media.publicId || 'Image'}
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: 1,
                                                    objectFit: 'cover',
                                                    mb: 0.5
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Stack>
                        );
                    } else if (Array.isArray(answer.answer)) {
                        return (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {answer.answer.map((value, index) => (
                                    <Chip
                                        key={index}
                                        label={String(value)}
                                        size="small"
                                        sx={{mb: 0.5}}
                                    />
                                ))}
                            </Stack>
                        );
                    } else if (typeof answer.answer === 'boolean') {
                        return (
                            <Chip
                                label={answer.answer ? t('Yes') : t('No')}
                                size="small"
                                color={answer.answer ? 'success' : 'default'}
                            />
                        );
                    } else {
                        return (
                            <Typography variant="body2">
                                {String(answer.answer)}
                            </Typography>
                        );
                    }
                }
                return null;
        }
    };

    // Custom fetch function that extracts unique question IDs
    const fetchDataWithQuestions = async (
        page: number,
        size: number,
        search: string,
        sortProp: string,
        sortDir: 'ASC' | 'DESC'
    ) => {
        // if (!formId) throw new Error('Form ID is required');

        const response = await FormService.getFormSubmissions({
            formId: !props.profileId && formId ? [formId] : undefined,
            profileId: props.profileId ? [props.profileId] : undefined,
            limit: {
                offset: page * size,
                limit: size,
                sort: [{field: "id", direction: "DESC"}]
            }
        });

        // Extract unique question IDs from all submissions
        if (response?.content && formQuery.data) {
            const allQuestionIds = new Set<string>();
            response.content?.forEach(submission => {
                submission.answers?.forEach(answer => {
                    // Prefer questionId if available, otherwise use questionTitle as fallback
                    if (formQuery.data && ('questionId' in answer && answer.questionId)) {
                        allQuestionIds.add(answer.questionId as string);
                    } else {
                        // For backward compatibility with old submissions that don't have questionId
                        allQuestionIds.add(answer.questionTitle);
                    }
                });
            });
            setUniqueQuestionIds(Array.from(allQuestionIds));
        }

        return response;
    };

    // UseList hook
    const {renderList} = useList({
        listId: 'form-submissions',
        list: (page, size, search, sortProp, sortDir, filters) => {
            return fetchDataWithQuestions(page, size, search, sortProp, sortDir)
        },
        columns,
        allColumns: columns,
        renderCell,
        defaultPageSize: 10,
        extractKey: (item) => item?.id || '',
        actionBar: props.profileId && !props.hideActionBar ? () => (
            <Stack direction="row" gap={2}>
                <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    onClick={() => setGenerateLinkDialogOpen(true)}
                >
                    {t('Generate Link')}
                </Button>
            </Stack>
        ) : undefined,
        emptyComponent: (
            <Card>
                <Stack alignItems="center" justifyContent="center" sx={{py: 8, px: 4}}>
                    <ArticleIcon
                        style={{marginBottom: 24, opacity: 0.5, color: theme.palette.primary.main}}
                    />

                    <Typography variant="h3" gutterBottom sx={{mb: 1}}>
                        {t('No submissions yet')}
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{mb: 4, maxWidth: 500, textAlign: 'center'}}>
                        {t('Form submissions will appear here once users start filling out your form')}
                    </Typography>
                </Stack>
            </Card>
        )
    }, formQuery.data?.id);

    return (
        <>
            {!props.profileId && !props.hideTitle && (
                <SettingsPageHeader
                    title={t('Form Submissions')}
                    back={() => navigate(formId ? `/system/forms/${formId}` : '/system/forms')}
                />
            )}

            <Stack sx={{
                overflowX: 'auto',
            }}>
            {renderList()}

            {/* Submission Detail Dialog */}
            {selectedSubmission && (
                <SubmissionDetailDialog
                    open={detailDialogOpen}
                    onClose={handleCloseDetailDialog}
                    submission={selectedSubmission}
                />
            )}

            {/* Generate Link Dialog */}
            {props.profileId && (
                <GenerateLinkDialog
                    open={generateLinkDialogOpen}
                    onClose={() => setGenerateLinkDialogOpen(false)}
                    profileId={props.profileId}
                />
            )}

            <Dialog
                open={previewDialogOpen}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
            >
                <DialogContent>
                    {previewMedia && (
                        <Box
                            component="img"
                            src={getScaledUrl(previewMedia, MediaSize.XLARGE_1000, MediaSize.XLARGE_1000)}
                            alt={t('Uploaded media')}
                            sx={{
                                display: 'block',
                                maxHeight: '70vh',
                                width: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview}>
                        {t('Close')}
                    </Button>
                    {previewMedia?.mediaURI && (
                        <Button
                            startIcon={<DownloadIcon/>}
                            onClick={() => handleDownload(previewMedia)}
                        >
                            {t('Download')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            </Stack>
        </>
    );
}
